"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { TerraLink } from "../../component/TerraLink/TerraLink"

type NavLink = {
  contentfulId?: string
  text: string
  url: string
  primary?: boolean
}

const getNavLinkKey = (link: NavLink, index: number): string =>
  link.contentfulId ?? `${link.url}-${link.text}-${index}`

export interface LayoutSubnavigationProps {
  links: NavLink[]
  cta?: NavLink
}

const HamburgerIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
  >
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
)

const CloseIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
  >
    <line x1="6" y1="6" x2="18" y2="18" />
    <line x1="6" y1="18" x2="18" y2="6" />
  </svg>
)

export const LayoutSubnavigation = (props: LayoutSubnavigationProps) => {
  const { links, cta } = props
  const [isFixed, setIsFixed] = useState(false)
  const [stickyTop, setStickyTop] = useState(0)
  const [navHeight, setNavHeight] = useState(0)
  const [menuOpen, setMenuOpen] = useState(false)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const navRef = useRef<HTMLElement>(null)

  const getSiteNavHeight = useCallback(() => {
    const siteNav = document.querySelector(
      ".site-navigation",
    ) as HTMLElement | null
    if (!siteNav) return 0
    return siteNav.classList.contains("site-navigation--is-hidden")
      ? 0
      : siteNav.offsetHeight
  }, [])

  useEffect(() => {
    let rafId: number

    const update = () => {
      const siteNavH = getSiteNavHeight()
      setStickyTop(siteNavH)

      if (sentinelRef.current) {
        const sentinelRect = sentinelRef.current.getBoundingClientRect()
        setIsFixed(sentinelRect.top < siteNavH)
      }
    }

    const onScroll = () => {
      cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(update)
    }

    if (navRef.current) {
      setNavHeight(navRef.current.offsetHeight)
    }

    update()
    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onScroll, { passive: true })

    const siteNav = document.querySelector(
      ".site-navigation",
    ) as HTMLElement | null
    let siteNavObserver: ResizeObserver | undefined
    if (siteNav) {
      siteNavObserver = new ResizeObserver(() => update())
      siteNavObserver.observe(siteNav)
    }

    let siteNavMutationObserver: MutationObserver | undefined
    if (siteNav) {
      siteNavMutationObserver = new MutationObserver(() => update())
      siteNavMutationObserver.observe(siteNav, {
        attributes: true,
        attributeFilter: ["class"],
      })
    }

    let navObserver: ResizeObserver | undefined
    if (navRef.current) {
      navObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          setNavHeight(entry.target.getBoundingClientRect().height)
        }
      })
      navObserver.observe(navRef.current)
    }

    return () => {
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onScroll)
      cancelAnimationFrame(rafId)
      siteNavObserver?.disconnect()
      siteNavMutationObserver?.disconnect()
      navObserver?.disconnect()
    }
  }, [getSiteNavHeight])

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 1280) setMenuOpen(false)
    }
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [])

  const hasCta = cta?.text && cta?.url
  const ctaPrimary = cta?.primary ?? true

  if (!links?.length) return null

  return (
    <>
      <div ref={sentinelRef} style={{ height: isFixed ? navHeight : 0 }} />

      <nav
        ref={navRef}
        className={`bg-gray-m2 text-gray-m10 z-[4] transition-[top] duration-200 ${
          isFixed ? "fixed left-0 right-0" : "relative"
        }`}
        style={isFixed ? { top: stickyTop } : undefined}
        data-testid="layout-subnavigation"
      >
        {/* ── Bar ─────────────────────────────────────────────────── */}
        <div className="flex flex-row items-center justify-between py-3 xl:py-5 px-4 xl:px-[calc((100%-1280px)/2)]">
          {/* Hamburger — visible below xl */}
          <button
            type="button"
            onClick={() => setMenuOpen(o => !o)}
            className="xl:hidden flex items-center justify-center p-1 -ml-1 text-gray-m10"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
          >
            {menuOpen ? <CloseIcon /> : <HamburgerIcon />}
          </button>

          {/* Horizontal links — visible at xl+ */}
          <ul className="hidden xl:flex list-none p-0 m-0 items-center overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            {links.map((link, index) => (
              <li
                key={getNavLinkKey(link, index)}
                className="px-3 xl:px-[25px] flex relative flex-shrink-0"
              >
                <TerraLink
                  linkType={"text"}
                  label={link?.text}
                  destination={link?.url}
                  tabIndex={0}
                  className="text-gray-m10 text-[.9375rem] not-italic font-bold leading-5 no-underline uppercase hover:text-gray-m5 hover:no-underline whitespace-nowrap"
                >
                  {link?.text}
                </TerraLink>
              </li>
            ))}
          </ul>

          {/* CTA — visible at xl+ */}
          {hasCta && (
            <div className="hidden xl:flex flex-shrink-0 mr-[21px]">
              <TerraLink
                linkType={"button"}
                label={cta.text}
                primary={ctaPrimary}
                destination={cta.url}
                tabIndex={0}
                className="py-[10px]! leading-5! whitespace-nowrap"
              >
                {cta.text}
              </TerraLink>
            </div>
          )}
        </div>

        {/* ── Dropdown — visible below xl when open ───────────────── */}
        <div
          className={`xl:hidden overflow-hidden transition-[max-height] duration-300 ease-in-out ${
            menuOpen ? "max-h-[500px]" : "max-h-0"
          }`}
        >
          <ul className="list-none m-0 px-4 pb-4 pt-1 flex flex-col gap-1">
            {links.map((link, index) => (
              <li key={getNavLinkKey(link, index)}>
                <TerraLink
                  linkType={"text"}
                  label={link?.text}
                  destination={link?.url}
                  tabIndex={menuOpen ? 0 : -1}
                  className="block py-2 text-gray-m10 text-[.9375rem] font-bold leading-5 no-underline uppercase hover:text-gray-m5 hover:no-underline"
                  onClickHandler={() => setMenuOpen(false)}
                >
                  {link?.text}
                </TerraLink>
              </li>
            ))}
          </ul>

          {hasCta && (
            <div className="px-4 pb-4">
              <TerraLink
                linkType={"button"}
                label={cta.text}
                primary={ctaPrimary}
                destination={cta.url}
                tabIndex={menuOpen ? 0 : -1}
                className="leading-5 whitespace-nowrap w-full text-center"
                onClickHandler={() => setMenuOpen(false)}
              >
                {cta.text}
              </TerraLink>
            </div>
          )}
        </div>
      </nav>
    </>
  )
}
